/* @section: main-page */
import { useState, useMemo } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { SECTORS, TASKS_BY_SECTOR } from "@/lib/sectors";
import { generateTable } from "@/lib/generator";
import { exportToExcel } from "@/lib/excelExport";
import type { GeneratedTable } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import TablePreview from "@/components/TablePreview";

type Step = "sector" | "task" | "configure";

const PROMPT_EXAMPLES = [
  "Ajouter une colonne TVA à 20 %, prévoir 20 lignes",
  "30 lignes, ajouter une colonne remise",
  "Avec TVA 5,5 % et une colonne commentaire",
];

const DynIcon = ({ name, className }: { name: string; className?: string }) => {
  const Cmp = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name] || Icons.Square;
  return <Cmp className={className} />;
};

const Index = () => {
  const [step, setStep] = useState<Step>("sector");
  const [sectorId, setSectorId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [table, setTable] = useState<GeneratedTable | null>(null);
  const [generating, setGenerating] = useState(false);

  const sector = useMemo(() => SECTORS.find((s) => s.id === sectorId) || null, [sectorId]);
  const tasks = useMemo(() => (sectorId ? TASKS_BY_SECTOR[sectorId] || [] : []), [sectorId]);
  const task = useMemo(() => tasks.find((t) => t.id === taskId) || null, [tasks, taskId]);

  const handleSelectSector = (id: string) => {
    setSectorId(id);
    setTaskId(null);
    setTable(null);
    setStep("task");
  };

  const handleSelectTask = (id: string) => {
    setTaskId(id);
    setTable(null);
    setStep("configure");
  };

  const handleGenerate = () => {
    if (!taskId) return;
    setGenerating(true);
    // petite latence pour le ressenti "génération"
    setTimeout(() => {
      try {
        const result = generateTable(taskId, prompt);
        if (!result || !result.columns.length) {
          throw new Error("Tableau vide");
        }
        setTable(result);
        toast.success("Tableau généré avec succès");
      } catch {
        toast.error("La génération a échoué. Reformulez votre demande et réessayez.");
      } finally {
        setGenerating(false);
      }
    }, 350);
  };

  const handleExport = () => {
    if (!table) return;
    try {
      exportToExcel(table);
      toast.success("Fichier Excel téléchargé");
    } catch {
      toast.error("Le téléchargement a échoué. Veuillez réessayer.");
    }
  };

  const reset = () => {
    setStep("sector");
    setSectorId(null);
    setTaskId(null);
    setPrompt("");
    setTable(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* @section: header */}
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-12">
          <button onClick={reset} className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Icons.Table2 className="h-5 w-5" />
            </span>
            <span className="text-lg font-semibold tracking-tight">ExcelGen Pro</span>
          </button>
          <span className="hidden text-sm text-muted-foreground sm:block">
            Générez vos fichiers Excel en quelques secondes — sans inscription
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 md:px-12 md:py-16">
        {/* @section: hero */}
        <div className="mb-10 text-center">
          <h1 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight md:text-4xl">
            Vos outils Excel métier, prêts en quelques clics
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground">
            Choisissez votre secteur, votre tâche, affinez en langage libre,
            prévisualisez puis téléchargez votre fichier <span className="font-medium text-foreground">.xlsx</span> avec formules intégrées.
          </p>
        </div>

        {/* @section: stepper */}
        <div className="mb-8 flex items-center justify-center gap-2 text-sm">
          <StepDot active={step === "sector"} done={!!sector} label="Secteur" />
          <Icons.ChevronRight className="h-4 w-4 text-muted-foreground/50" />
          <StepDot active={step === "task"} done={!!task} label="Tâche" />
          <Icons.ChevronRight className="h-4 w-4 text-muted-foreground/50" />
          <StepDot active={step === "configure"} done={!!table} label="Personnaliser" />
        </div>

        {/* @section: sector-selector */}
        {step === "sector" && (
          <section>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {SECTORS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleSelectSector(s.id)}
                  className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-6 text-left transition-all duration-200 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 active:scale-[0.99]"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <DynIcon name={s.icon} className="h-6 w-6" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-base font-semibold">{s.label}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">{s.description}</span>
                  </span>
                  <Icons.ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground/40 transition-all group-hover:translate-x-1 group-hover:text-primary" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* @section: task-selector */}
        {step === "task" && sector && (
          <section>
            <div className="mb-5 flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("sector")} className="gap-1.5">
                <Icons.ArrowLeft className="h-4 w-4" /> Retour
              </Button>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <DynIcon name={sector.icon} className="h-4 w-4 text-primary" />
                {sector.label}
              </span>
            </div>
            <h2 className="mb-4 text-lg font-semibold">Quelle tâche souhaitez-vous gérer ?</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {tasks.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleSelectTask(t.id)}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 text-left transition-all duration-200 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 active:scale-[0.99]"
                >
                  <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Icons.FileSpreadsheet className="h-5 w-5" />
                  </span>
                  <span className="text-base font-semibold">{t.label}</span>
                  <span className="mt-1 text-sm text-muted-foreground">{t.description}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* @section: configure */}
        {step === "configure" && sector && task && (
          <section className="grid grid-cols-1 gap-8 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="mb-5 flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep("task")} className="gap-1.5">
                  <Icons.ArrowLeft className="h-4 w-4" /> Retour
                </Button>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <DynIcon name={sector.icon} className="h-3.5 w-3.5 text-primary" />
                  {sector.label}
                </div>
                <h2 className="text-lg font-semibold">{task.label}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>

                <label className="mt-5 block text-sm font-medium">
                  Affinez votre demande <span className="font-normal text-muted-foreground">(optionnel)</span>
                </label>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Ex : Ajouter une colonne TVA à 20 %, prévoir 20 lignes, en français…"
                  className="mt-2 min-h-[110px] resize-none"
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  {PROMPT_EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      onClick={() => setPrompt(ex)}
                      className="rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      {ex}
                    </button>
                  ))}
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="mt-5 h-12 w-full gap-2 text-base"
                >
                  {generating ? (
                    <>
                      <Icons.Loader2 className="h-5 w-5 animate-spin" /> Génération…
                    </>
                  ) : (
                    <>
                      <Icons.Wand2 className="h-5 w-5" /> {table ? "Régénérer" : "Générer le tableau"}
                    </>
                  )}
                </Button>
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  L'outil interprète secteur + tâche + votre demande pour construire le tableau.
                </p>
              </div>
            </div>

            {/* @section: preview-panel */}
            <div className="lg:col-span-3">
              {table ? (
                <div className="rounded-2xl border border-border bg-card p-6">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold">{table.title}</h3>
                      <p className="text-xs text-muted-foreground">Aperçu du tableau généré</p>
                    </div>
                    <Button onClick={handleExport} className="h-11 gap-2">
                      <Icons.Download className="h-5 w-5" /> Télécharger en Excel
                    </Button>
                  </div>
                  <TablePreview table={table} />
                </div>
              ) : (
                <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/20 p-10 text-center">
                  <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icons.TableProperties className="h-7 w-7" />
                  </span>
                  <p className="text-sm font-medium">Votre aperçu apparaîtra ici</p>
                  <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                    Cliquez sur « Générer le tableau » pour visualiser les colonnes, formules et données d'exemple.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* @section: footer */}
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-6 py-6 text-xs text-muted-foreground sm:flex-row md:px-12">
          <span>ExcelGen Pro — Accès libre, sans compte ni inscription.</span>
          <span>Fichiers .xlsx générés localement dans votre navigateur.</span>
        </div>
      </footer>
    </div>
  );
};

const StepDot = ({ active, done, label }: { active: boolean; done: boolean; label: string }) => (
  <span
    className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-colors ${
      active
        ? "bg-primary/10 font-medium text-primary"
        : done
        ? "text-foreground"
        : "text-muted-foreground"
    }`}
  >
    {done && !active ? <Icons.CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : null}
    {label}
  </span>
);

export default Index;
